/**
 * <magnify-dialog> — swipe, pan and zoom for Dawn's product media modal.
 *
 * Dawn still owns the modal, its focus trap and ESC. This element adds:
 * - phone (below 750px): every image in a horizontal swipe track, one per
 *   screen, with previous/next buttons and a counter. Stock Dawn shows only
 *   the tapped image there, at 1100px wide, with no way to reach the others;
 * - zoom state for one image at a time (pinch, double tap, click, wheel and
 *   the +/- buttons), clamped so a zoomed image never shows a gap at an edge.
 *
 * Input: magnify-gestures.js. Buttons: magnify-controls.js. Maths: magnify-geometry.js.
 */
if (!customElements.get('magnify-dialog')) {
  customElements.define(
    'magnify-dialog',
    class MagnifyDialog extends HTMLElement {
      static MIN_SCALE = 1;
      static MAX_SCALE = 4;
      static TAP_ZOOM = 2.5;
      static STEP = 0.5;
      static SETTLE_SCALE = 1.05;

      constructor() {
        super();
        this.geometry = window.MagnifyGeometry;
        this.state = { image: null, scale: 1, x: 0, y: 0, slideIndex: -1 };
        this.gesturing = false;
        this.phone = window.matchMedia('(max-width: 749px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      }

      connectedCallback() {
        // Dawn's <product-modal> moves itself to <body> once it upgrades, which
        // disconnects and reconnects this element: bind once, re-observe after.
        if (this.gestures) return this.watchModal();
        this.modal = this.closest('product-modal');
        this.stage = this.modal?.querySelector('.product-media-modal__content');
        if (!this.stage || !this.geometry || !window.MagnifyGestures || !window.MagnifyControls) return;
        this.stage.classList.add('magnify-dialog__stage');
        this.renderControls();
        this.gestures = new window.MagnifyGestures(this);
        this.gestures.bind();
        this.stage.addEventListener('scroll', () => this.onStageScroll(), { passive: true });
        this.stage.addEventListener('keydown', this.onKeyDown.bind(this));
        this.watchModal();
        this.updateNavigation();
      }

      disconnectedCallback() {
        this.observers?.forEach((observer) => observer.disconnect());
      }

      renderControls() {
        const controls = window.MagnifyControls.render(this.modal.dataset);
        Object.assign(this, controls.nodes);
        this.replaceChildren(controls.root);
        // Dawn closes the media modal on any mouse pointerup inside it; the
        // buttons here must not count as a click on the backdrop.
        this.addEventListener('pointerup', (event) => event.stopPropagation());

        const { previous, next, zoomIn, zoomOut, reset } = controls.buttons;
        previous.addEventListener('click', () => this.goToSlide(this.currentIndex() - 1));
        next.addEventListener('click', () => this.goToSlide(this.currentIndex() + 1));
        zoomIn.addEventListener('click', () => this.stepZoom(MagnifyDialog.STEP));
        zoomOut.addEventListener('click', () => this.stepZoom(-MagnifyDialog.STEP));
        reset.addEventListener('click', () => this.reset());
      }

      watchModal() {
        const onOpenChange = () => {
          if (!this.modal.hasAttribute('open')) return this.reset();
          // Dawn's showActiveMedia() sets scrollLeft from the image width, which
          // throws the swipe track back to its first image: put the opened one back.
          const index = this.slides().findIndex((slide) => slide.querySelector('.active'));
          if (this.isCarousel() && index >= 0) this.stage.scrollLeft = index * this.stage.clientWidth;
          this.updateNavigation();
        };
        const onContentChange = () => {
          this.reset();
          this.updateNavigation();
        };
        this.observers = [new MutationObserver(onOpenChange), new MutationObserver(onContentChange)];
        this.observers[0].observe(this.modal, { attributes: true, attributeFilter: ['open'] });
        // product-info.js replaces the modal's content on every variant change.
        this.observers[1].observe(this.stage, { childList: true });
      }

      onKeyDown(event) {
        if (event.key === '+' || event.key === '=') this.stepZoom(MagnifyDialog.STEP);
        else if (event.key === '-') this.stepZoom(-MagnifyDialog.STEP);
      }

      /* ---------- Slides ---------- */

      isCarousel() {
        return this.phone.matches;
      }

      slides() {
        return Array.from(this.stage.children);
      }

      currentIndex() {
        return this.geometry.slideIndex(this.stage.scrollLeft, this.stage.clientWidth, this.slides().length);
      }

      goToSlide(index) {
        if (!this.isCarousel() || index < 0 || index >= this.slides().length) return;
        this.reset();
        const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';
        this.stage.scrollTo({ left: index * this.stage.clientWidth, behavior });
      }

      /** Lines the track up on one image before it locks for a zoom. */
      snapToSlide() {
        if (this.isCarousel()) this.stage.scrollLeft = this.currentIndex() * this.stage.clientWidth;
      }

      /**
       * While an image is zoomed (or a pinch is running) the track must not
       * move: iOS can keep a swipe's momentum going after overflow switches to
       * hidden, which scrolled the next image into view (Mauro's phone, 132%).
       */
      pinTrack() {
        if (!this.isCarousel() || this.state.slideIndex < 0) return;
        const left = this.state.slideIndex * this.stage.clientWidth;
        if (Math.abs(this.stage.scrollLeft - left) > 0.5) this.stage.scrollLeft = left;
      }

      isLocked() {
        return this.gesturing || this.state.scale > MagnifyDialog.MIN_SCALE;
      }

      onStageScroll() {
        if (this.isLocked()) this.pinTrack();
        if (this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateNavigation();
        });
      }

      updateNavigation() {
        const count = this.slides().length;
        const index = this.currentIndex();
        window.MagnifyControls.update(this, index, count, this.modal.dataset.magnifyCounter);
        this.classList.toggle('magnify-dialog--single', count < 2);
      }

      /** Images live one per slide (snippets/product-media-modal.liquid). */
      imageFromTarget(target) {
        const image = target?.closest?.('img');
        const slide = image?.parentElement;
        return slide && slide.parentElement === this.stage ? image : null;
      }

      currentImage() {
        if (this.state.image) return this.state.image;
        const slide = this.isCarousel() ? this.slides()[this.currentIndex()] : null;
        return slide?.querySelector(':scope > img') || null;
      }

      /* ---------- Zoom ---------- */

      clampScale(scale) {
        return this.geometry.clamp(scale, MagnifyDialog.MIN_SCALE, MagnifyDialog.MAX_SCALE);
      }

      setActiveImage(image) {
        if (this.state.image === image) return;
        if (this.state.image) this.resetImage(this.state.image);
        this.state = { image, scale: 1, x: 0, y: 0, slideIndex: this.slides().indexOf(image.parentElement) };
      }

      toggleZoom(image, point) {
        if (!image) return;
        this.setActiveImage(image);
        this.zoomTo(this.state.scale > 1 ? MagnifyDialog.MIN_SCALE : MagnifyDialog.TAP_ZOOM, point);
      }

      stepZoom(delta) {
        const image = this.currentImage() || this.stage.querySelector(':scope > * > img');
        if (!image) return;
        this.setActiveImage(image);
        const rect = image.getBoundingClientRect();
        this.zoomTo(this.state.scale + delta, { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
      }

      /** Zooms to `scale` keeping `point` (client coordinates) where it is. */
      zoomTo(scale, point) {
        if (!this.state.image) return;
        const next = this.clampScale(scale);
        if (point && next > MagnifyDialog.MIN_SCALE) {
          const box = this.box();
          const { geometry, state } = this;
          state.x = geometry.offsetAround(state.x, state.scale, next, point.x - box.viewLeft, box.left);
          state.y = geometry.offsetAround(state.y, state.scale, next, point.y - box.viewTop, box.top);
        }
        this.state.scale = next;
        this.applyTransform();
      }

      /** After a gesture, a scale that only just left 1x snaps back to it. */
      settle() {
        if (this.state.image && this.state.scale < MagnifyDialog.SETTLE_SCALE) this.zoomTo(MagnifyDialog.MIN_SCALE);
      }

      setGesturing(active) {
        this.gesturing = active;
        this.state.image?.classList.toggle('is-gesturing', active);
        this.stage.classList.toggle('magnify-dialog__stage--locked', active || this.state.scale > 1);
        if (active) this.pinTrack();
      }

      /**
       * Untransformed image box, measured from its slide's top-left: the slide
       * clips it. Layout offsets, not getBoundingClientRect(): mid-transition
       * (a second tap on + or a pinch during the double-tap animation) the
       * rect is the animated value, which threw the clamp off and pushed the
       * image up and off-centre. The slide is position: relative, so it is the
       * image's offsetParent.
       */
      box() {
        const { image } = this.state;
        const slide = image.parentElement;
        const view = slide.getBoundingClientRect();
        return {
          left: image.offsetLeft,
          top: image.offsetTop,
          width: image.offsetWidth,
          height: image.offsetHeight,
          viewLeft: view.left,
          viewTop: view.top,
          viewWidth: slide.clientWidth,
          viewHeight: slide.clientHeight,
        };
      }

      applyTransform() {
        const { image, scale } = this.state;
        if (!image) return;
        if (scale <= MagnifyDialog.MIN_SCALE) {
          this.state.x = 0;
          this.state.y = 0;
        } else {
          const box = this.box();
          this.state.x = this.geometry.clampAxis(this.state.x, box.left, box.width, scale, box.viewWidth);
          this.state.y = this.geometry.clampAxis(this.state.y, box.top, box.height, scale, box.viewHeight);
        }
        const zoomed = scale > MagnifyDialog.MIN_SCALE;
        image.style.transform = zoomed ? `translate(${this.state.x}px, ${this.state.y}px) scale(${scale})` : '';
        image.classList.toggle('is-zoomed', zoomed);
        this.classList.toggle('magnify-dialog--active', zoomed);
        this.stage.classList.toggle('magnify-dialog__stage--locked', this.gesturing || zoomed);
        if (this.gesturing || zoomed) this.pinTrack();
        this.levelNode.textContent = `${Math.round(scale * 100)}%`;
      }

      resetImage(image) {
        image.style.transform = '';
        image.classList.remove('is-zoomed', 'is-panning', 'is-gesturing');
      }

      reset() {
        if (this.state.image) this.resetImage(this.state.image);
        this.state = { image: null, scale: 1, x: 0, y: 0, slideIndex: -1 };
        this.gesturing = false;
        this.gestures?.clear();
        this.classList.remove('magnify-dialog--active');
        this.stage.classList.remove('magnify-dialog__stage--locked');
        if (this.levelNode) this.levelNode.textContent = '100%';
      }
    }
  );
}

/** Attaches one <magnify-dialog> to every product media modal on the page. */
function attachMagnifyDialogs() {
  document.querySelectorAll('product-modal').forEach((modal) => {
    const dialog = modal.querySelector('.product-media-modal__dialog');
    if (dialog && !modal.querySelector('magnify-dialog')) dialog.appendChild(document.createElement('magnify-dialog'));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', attachMagnifyDialogs);
} else {
  attachMagnifyDialogs();
}
